import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  translateJobFields,
  type JobTranslatedFields,
} from '../../../services/jobContentTranslation';

interface JobContentSource {
  id: string;
  title: string;
  description?: string;
}

export interface UseJobContentTranslationResult {
  title: string;
  description?: string;
  isTranslating: boolean;
}

/**
 * Translates job title + description when the active locale is not English.
 * Falls back to source text while loading or on error.
 */
export function useJobContentTranslation(
  job: JobContentSource | null | undefined,
  options?: { translateDescription?: boolean },
): UseJobContentTranslationResult {
  const { i18n } = useTranslation();
  const locale = i18n.language.split('-')[0]?.toLowerCase() || 'en';
  const translateDescription = options?.translateDescription !== false;

  const [translated, setTranslated] = useState<JobTranslatedFields | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (!job) {
      setTranslated(null);
      setIsTranslating(false);
      return;
    }

    if (locale === 'en') {
      setTranslated(null);
      setIsTranslating(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setIsTranslating(true);
    void translateJobFields(
      job.id,
      locale,
      {
        title: job.title,
        description: translateDescription ? job.description : undefined,
      },
      controller.signal,
    )
      .then((result) => {
        if (!cancelled) setTranslated(result);
      })
      .catch(() => {
        if (!cancelled) setTranslated(null);
      })
      .finally(() => {
        if (!cancelled) setIsTranslating(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [job?.id, job?.title, job?.description, locale, translateDescription]);

  return {
    title: translated?.title ?? job?.title ?? '',
    description: translated?.description ?? job?.description,
    isTranslating: isTranslating && locale !== 'en',
  };
}
